import {
  DevHubFormControl,
  DevHubTypography,
  DevHubIconCardGrid,
} from '@devxdiscover/devhub-ui';
import type { DevHubIconCardItem } from '@devxdiscover/devhub-ui';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { UIOptionsType } from '@rjsf/utils';
import { makeStyles } from '@material-ui/core/styles';

export type IconCardPickerOptions = UIOptionsType & {
  items: DevHubIconCardItem[];
  columns?: number;
};

const useStyles = makeStyles(theme => ({
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
}));

/**
 * Schema for the icon card picker custom field
 */
export const IconCardPickerSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      items: {
        title: 'Items',
        type: 'array',
        description:
          'Array of selectable items, each with value, label, and iconKey/iconUrl/icon',
        items: {
          type: 'object',
          properties: {
            value: { type: 'string', title: 'Value' },
            label: { type: 'string', title: 'Label' },
            description: { type: 'string', title: 'Description' },
            icon: {
              type: 'string',
              title: 'Material UI Icon Name',
              description:
                'A Material UI icon name (e.g. FolderOpen, Cloud). Lowest precedence.',
            },
            iconKey: {
              type: 'string',
              title: 'DevHub Package Icon Key',
              description:
                'A devhub-ui package icon key (e.g. maven, gradle, npm, terraform, generic).',
            },
            iconUrl: {
              type: 'string',
              title: 'Icon URL',
              description: 'Direct image URL. Highest precedence.',
            },
          },
        },
      },
      columns: {
        title: 'Columns',
        type: 'number',
        description: 'Number of columns in the grid (default: 4)',
        default: 4,
      },
    },
  },
  returnValue: {
    type: 'string',
  },
};

/**
 * Icon card picker component
 * @param props - Props for the icon card picker
 * @constructor
 */
export const IconCardPicker = (
  props: FieldExtensionComponentProps<string, IconCardPickerOptions>,
) => {
  const { onChange, formData, schema, required, rawErrors, uiSchema } = props;
  const classes = useStyles();
  const opts = (uiSchema['ui:options'] ?? {}) as IconCardPickerOptions;
  const items = opts.items ?? [];
  const columns = opts.columns ?? 4;

  const title = schema.title ?? 'Select an option';
  const description = schema.description;

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0}
    >
      <DevHubTypography variant="subtitle1" gutterBottom>
        {title}
        {required ? ' *' : ''}
      </DevHubTypography>
      {description && (
        <DevHubTypography variant="body2" color="textSecondary" gutterBottom>
          {description}
        </DevHubTypography>
      )}

      <DevHubIconCardGrid
        items={items}
        selectedValue={formData}
        onChange={onChange}
        columns={columns}
      />

      {rawErrors && rawErrors.length > 0 && (
        <DevHubTypography variant="caption" className={classes.errorText}>
          {rawErrors.join(', ')}
        </DevHubTypography>
      )}
    </DevHubFormControl>
  );
};
